/* eslint-disable array-bracket-newline */
import { Suspense, useEffect } from 'react';
import { Await, PathMatch, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { fromBase64 } from 'js-base64';
import { Network, enums } from '@kattana-io/common';
import SearchTable, {
  IMobileFilterProps,
} from '../../../components/SearchTable/SearchTable';
import NewPairsTableMobile from './NewPairsTable';
import {
  TableFilterOption,
  TableFilterSelect,
} from '../../../components/TableComponents/Filter';
import Fallback from '../../../components/Fallback';
import { newPairsPageLoaderPDataType } from '../../loaders/newPairsLoader';
import TableFilterMobile, {
  TableFilterMobileData,
} from '../../../components/TableComponents/Filter/mobile';
import { CHAIN_SELECTOR_OPTIONS } from '../../../components/ChainSelector';
import { DEFAULT_URL_PATH } from '../../../helpers/constants';
import TableSortMobile, {
  TableSortMobileData,
} from '../../../components/TableComponents/Sort/mobile';
import { ITableSort } from '../../../molecules/Table/DesktopTable';
import { IRates } from '../../../reducers/rates';
import NewPairsTableMobilePlaceholder from './Placeholder';

import './styles.scss';

const NEW_PAIRS_SORT_OPTIONS = [
  {
    title: 'Listed since:',
    id: 'pool_created',
    optionHigh: 'Recent pairs',
    optionLow: 'Old pairs',
  },
  {
    title: 'Total liquidity:',
    id: 'liquidity',
    optionHigh: 'High liquidity',
    optionLow: 'Low liquidity',
  },
  {
    title: '24H Volume:',
    id: 'volume',
    optionHigh: 'High volume',
    optionLow: 'Low volume',
  },
];

interface INewPairsBodyMobileProps {
  tickers: unknown;
  rates: IRates;
  chain: string;
  page: string;
  plan: enums.PricingPlan;
  liquidityFilterOptions: TableFilterOption[];
  liquidity?: string | null;
  loading: false | PathMatch<string> | null;
  sort: string;
  onSetChain(chain: string): void;
  onSetPage(page: number): void;
  onLiquidityFilterSelect: TableFilterSelect;
  handleSort(newSort: ITableSort): void;
}

const NewPairsBodyMobile = ({
  tickers,
  rates,
  chain,
  page,
  plan,
  liquidityFilterOptions,
  liquidity,
  loading,
  sort,
  onSetChain,
  onSetPage,
  onLiquidityFilterSelect,
  handleSort,
}: INewPairsBodyMobileProps) => {
  const navigate = useNavigate();

  const setFilteredData = ({ id, value }: TableFilterMobileData) => {
    if (id === 'liquidity') {
      return onLiquidityFilterSelect(value);
    }
  };

  const setSortedData = (newSort: TableSortMobileData) => {
    handleSort(newSort);
  };

  const renderTable = (
    search: string,
    onChildMount: (data: IMobileFilterProps) => void,
    onBack: () => void
  ) => {
    useEffect(() => {
      const network = Network.fromChain(chain);
      onChildMount({
        setFilterValue: setFilteredData,
        setSortValue: setSortedData,
        tabSelectorData: {
          options: CHAIN_SELECTOR_OPTIONS,
          onSelect: onSetChain,
          initial: {
            label: network.getTitle(),
            value: network.getSlug(),
            image: network.getImage(),
          },
          width: 150,
          height: '100%',
        },
      });
    }, [chain]);

    return (
      <Suspense fallback={<NewPairsTableMobilePlaceholder />}>
        <Await resolve={tickers} errorElement={<div>Error Loading</div>}>
          {(data: newPairsPageLoaderPDataType) => (
            <ErrorBoundary
              fallbackRender={(props) => (
                <Fallback
                  {...props}
                  className='centrify'
                  title='Big Trades Table'
                />
              )}
              onReset={(details) => {
                // Reset the state of your app so the error doesn't happen again
              }}
            >
              <NewPairsTableMobile
                data={data || []}
                inputSearch={search}
                loading={!!loading}
                rates={rates}
                page={page}
                onSetPage={onSetPage}
              />
            </ErrorBoundary>
          )}
        </Await>
      </Suspense>
    );
  };

  const renderFilterOptions = (props: IMobileFilterProps) => (
    <TableFilterMobile
      {...props}
      filters={{ liquidity }}
      items={[
        {
          title: 'Liquidity:',
          id: 'liquidity',
          options: liquidityFilterOptions,
        },
      ]}
    />
  );

  const renderSortOptions = (props: IMobileFilterProps) => (
    <TableSortMobile
      {...props}
      sort={JSON.parse(fromBase64(sort))}
      items={NEW_PAIRS_SORT_OPTIONS}
    />
  );

  const onBack = () => {
    navigate(DEFAULT_URL_PATH);
  };

  return (
    <SearchTable
      tableTitle='New Pairs'
      filterTitle='Select filter'
      sortTitle='Sorting by'
      renderChildren={renderTable}
      renderFilterOptions={
        plan === enums.PricingPlan.premium ? renderFilterOptions : undefined
      }
      renderSortOptions={renderSortOptions}
      onBack={onBack}
    />
  );
};

export default NewPairsBodyMobile;
